import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../utils/formatUtils';
import useApi from '../../../hooks/useApi';
import { fetchRecentReports } from '../../../services/dashboardService';

// Custom hook for fetching recent reports
const useRecentReports = () => {
  const { data, loading, error, callApi } = useApi(fetchRecentReports,{showSuccessToast: false});
  React.useEffect(() => { callApi(); }, [callApi]);
  return { data, loading, error };
};

export default function RecentReportsTable() {
  const navigate = useNavigate();
  const { data: recentReports, loading, error } = useRecentReports();


  if (loading) {
    return (
      <table className="min-w-full"><tbody><tr><td colSpan={8} className="px-6 py-4 text-center text-gray-500">Loading...</td></tr></tbody></table>
    );
  }
  if (error) {
    return (
      <table className="min-w-full"><tbody><tr><td colSpan={8} className="px-6 py-4 text-center text-red-500">Failed to load recent reports.</td></tr></tbody></table>
    );
  }
  if (!recentReports || recentReports.length === 0) {
    return (
      <table className="min-w-full"><tbody><tr><td colSpan={8} className="px-6 py-4 text-center text-gray-400">No recent reports found.</td></tr></tbody></table>
    );
  }

  function handleView(report) {
    switch (report.type) {
      case 'Expense':
        navigate(`/dashboard/expense-reports/${report.id}`);
        break;
      case 'Revenue':
        navigate(`/dashboard/revenue-reports/${report.id}`);
        break;
      case 'Bill':
        navigate(`/dashboard/bills/${report.id}`);
        break;
      case 'Proforma':
        navigate(`/dashboard/proformas/${report.id}`);
        break;
      default:
        break;
    }
  }
  
  function handleEdit(report) {
    switch (report.type) {
      case 'Expense':
        navigate(`/dashboard/expense-reports/${report.id}/edit`);
        break;
      case 'Revenue':
        navigate(`/dashboard/revenue-reports/${report.id}/edit`);
        break;
      case 'Bill':
        navigate(`/dashboard/bills/${report.id}/edit`);
        break;
      case 'Proforma':
        navigate(`/dashboard/proformas/edit/${report.id}`);
        break;
      default:
        break;
    }
  }

  return (
    <table className="min-w-full divide-y divide-gray-200">
      <thead>
        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
          <th className="px-6 py-3">Title</th>
          <th className="px-6 py-3">Type</th>
          {/* <th className="px-6 py-3">Creator</th> */}
          <th className="px-6 py-3">Edited by</th>
          {/* <th className="px-6 py-3">Date</th>
          <th className="px-6 py-3">Last Modified</th> */}
          <th className="px-6 py-3">Amount</th>
          {/* <th className="px-6 py-3">Status</th> */}
          <th className="px-6 py-3 text-right">Action</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {recentReports.map(report => (
          <tr key={`${report.type}-${report.id}`}>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{report.title}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700">{report.type}</span>
            </td>
            {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{report.creator}</td> */}
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{report.updated_by}</td>
            {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(report.date_created).toLocaleString()}</td> */}
            {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(report.date_modified).toLocaleString()}</td> */}
            <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatCurrency(report.amount)}</td>
            {/* <td className="px-6 py-4 whitespace-nowrap">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{report.status}</span>
            </td> */}
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex items-center justify-end gap-2">
              <button title="View" className="text-primary-600 hover:text-primary-800 p-1" aria-label="View Details" onClick={() => handleView(report)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              </button>

              <button title="Edit" className="text-yellow-600 hover:text-yellow-800 p-1" aria-label="Edit" onClick={() => handleEdit(report)}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13h3l8-8a2.828 2.828 0 00-4-4l-8 8v3zm0 0v3a2 2 0 002 2h3" /></svg>
              </button>
              <button title="Download" className="text-green-600 hover:text-green-800 p-1" aria-label="Download">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" /></svg>
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
